import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api-client";
import { createWorkspace, listWorkspaces, type Workspace } from "@/services/workspace.service";
import { defaultWorkspace, firstWorkspaceName } from "@/utils/workspace-rules";

interface WorkspaceContextType {
  currentWorkspaceId: string | null;
  setCurrentWorkspaceId: (id: string) => void;
  isLoading: boolean;
  /** The signed-in user: what every workspace query is keyed by. */
  userId: string | null;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const STORAGE_KEY = 'current_workspace_id';
const USER_KEY = 'current_user_id';

/**
 * How long the list answers access checks from the cache. The check below runs
 * on every navigation; without this each page change would be a round trip to
 * the `workspace` function. Every write the dashboard makes refreshes it, and
 * the server refuses a workspace the user has lost whatever this says.
 */
const WORKSPACES_FRESH_MS = 30_000;

// Helper to extract workspace ID from URL path
const getWorkspaceIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/([a-f0-9-]{36})/i);
  return match ? match[1] : null;
};

/**
 * A brand-new user's first workspace. Another tab can create it first: the
 * server then refuses this one — the free plan's cap, or the name — and the one
 * that tab made is used instead.
 */
async function createFirstWorkspace(email: string | undefined): Promise<Workspace | undefined> {
  try {
    return await createWorkspace(firstWorkspaceName(email));
  } catch (error) {
    if (error instanceof ApiError && (error.status === 403 || error.status === 409)) {
      return defaultWorkspace(await listWorkspaces());
    }
    throw error;
  }
}

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const params = useParams<{ workspaceId?: string }>();
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState<string | null>(() => {
    // First priority: URL params
    const urlWorkspaceId = params.workspaceId || getWorkspaceIdFromPath(window.location.pathname);
    if (urlWorkspaceId) {
      return urlWorkspaceId;
    }
    // Second priority: localStorage
    return localStorage.getItem(STORAGE_KEY);
  });
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isCreatingWorkspace = useRef(false);

  const setCurrentWorkspaceId = (id: string) => {
    navigate(`/${id}/agents`);
  };

  // A different user signing in on this browser drops the remembered workspace.
  useEffect(() => {
    if (!user) return;
    const savedUserId = localStorage.getItem(USER_KEY);
    if (savedUserId && savedUserId !== user.id) {
      localStorage.removeItem(STORAGE_KEY);
      setCurrentWorkspaceIdState(null);
    }
    localStorage.setItem(USER_KEY, user.id);
  }, [user]);

  useEffect(() => {
    const open = (id: string, redirect: boolean) => {
      setCurrentWorkspaceIdState(id);
      localStorage.setItem(STORAGE_KEY, id);
      if (redirect) navigate(`/${id}/agents`, { replace: true });
    };

    const initializeWorkspace = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Every workspace the user owns or belongs to: the server's answer is
        // what access means. Shared with `useWorkspaces`, which lists the same.
        const workspaces = await queryClient.fetchQuery({
          // Keyed by user: a list cached for the last user to sign in on this
          // browser must never answer for this one.
          queryKey: ["workspaces", user.id],
          queryFn: listWorkspaces,
          staleTime: WORKSPACES_FRESH_MS,
        });

        if (workspaces.length === 0) {
          // One creation at a time: the effect re-runs as the URL changes.
          if (isCreatingWorkspace.current) return;
          isCreatingWorkspace.current = true;
          try {
            const first = await createFirstWorkspace(user.email);
            // The cached empty list is wrong now, for the switcher too.
            await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
            if (first) open(first.id, true);
          } finally {
            isCreatingWorkspace.current = false;
          }
          return;
        }

        const fallback = defaultWorkspace(workspaces)!;
        const canOpen = (id: string | null | undefined) => workspaces.some((w) => w.id === id);

        // Verify cached workspace access - clear if no longer valid
        if (!canOpen(localStorage.getItem(STORAGE_KEY))) {
          localStorage.removeItem(STORAGE_KEY);
        }

        if (params.workspaceId) {
          // No access to the one in the URL: back to the user's own.
          if (canOpen(params.workspaceId)) open(params.workspaceId, false);
          else open(fallback.id, true);
          return;
        }

        // No workspace in the URL: the default, and only navigate if not already there.
        open(fallback.id, !location.pathname.includes(`/${fallback.id}/`));
      } catch (error) {
        console.error('Error initializing workspace:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeWorkspace();
  }, [user, params.workspaceId, navigate, location.pathname, queryClient]);

  // Sync state when URL params change
  useEffect(() => {
    const urlWorkspaceId = params.workspaceId || getWorkspaceIdFromPath(location.pathname);
    if (urlWorkspaceId && currentWorkspaceId !== urlWorkspaceId) {
      setCurrentWorkspaceIdState(urlWorkspaceId);
      localStorage.setItem(STORAGE_KEY, urlWorkspaceId);
    }
  }, [params.workspaceId, location.pathname, currentWorkspaceId]);

  return (
    <WorkspaceContext.Provider value={{ currentWorkspaceId, setCurrentWorkspaceId, isLoading, userId: user?.id ?? null }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};
